import MAccordion, { accordionClasses } from "@mui/material/Accordion";
import MAccordionDetails from "@mui/material/AccordionDetails";
import MAccordionSummary, { accordionSummaryClasses } from "@mui/material/AccordionSummary";
import MBadge, { badgeClasses } from "@mui/material/Badge";
import Button, { buttonClasses } from "@mui/material/Button";
import MCheckbox, { checkboxClasses } from "@mui/material/Checkbox";
import MFormControlLabel from "@mui/material/FormControlLabel";
import MMenu, { menuClasses } from "@mui/material/Menu";
import MMenuItem from "@mui/material/MenuItem";
import MPaper from "@mui/material/Paper";
import MPopover from "@mui/material/Popover";
import MRadio, { radioClasses } from "@mui/material/Radio";
import { styled } from "@mui/material/styles";
import { colors } from "@zapalacorp/tokens";

export const Popover = styled(MPopover)({});

export const PopoverButton = styled(Button, {
  shouldForwardProp(propName) {
    return propName !== "isActive";
  },
})<{ isActive?: boolean }>(({ isActive }) => {
  const extraStyle = !isActive
    ? {
        border: `1px solid ${colors.gray200} !important`,
        backgroundColor: `${colors.white} !important`,
        color: `${colors.gray600} !important`,
      }
    : {};

  return {
    ...extraStyle,
    borderRadius: "100px !important",

    [`&.${buttonClasses.root}`]: {
      fontFamily: "Manrope",
      textTransform: "none !important",
      fontWeight: 600,
      fontSize: "12px",
      letterSpacing: "0.5px",
    },
  };
});

export const Paper = styled(MPaper)(() => ({
  width: "320px",
  borderRadius: "12px !important",
}));

export const Accordion = styled(MAccordion)(() => ({
  borderRadius: "12px !important",
  "&:before": {
    display: "none",
  },
  [`&.${accordionClasses.root}`]: {
    borderTopLeftRadius: "4px",
    borderTopRightRadius: "4px",
    borderBottomLeftRadius: "4px",
    borderBottomRightRadius: "4px",
  },
  [`&.${accordionClasses.expanded}`]: {
    margin: "0 !important",
  },
}));

export const AccordionSummary = styled(MAccordionSummary)(() => ({
  padding: "16px !important",
  color: "#475467",
  fontWeight: 600,
  lineHeight: "24px",
  fontSize: "16px",
  letterSpacing: "0.5px",
  height: "52px",
  fontFamily: "Manrope",

  [`&.${accordionSummaryClasses.root}`]: {
    minHeight: "auto !important",
  },

  [`& .${accordionSummaryClasses.content}`]: {
    margin: "0 !important",
  },
}));

export const AccordionDetails = styled(MAccordionDetails)(() => ({
  padding: "16px !important",
  backgroundColor: colors.gray50,
}));

export const Header = styled("header")({
  padding: "16px !important",
  display: "flex",
  flexDirection: "column",
  gap: "4px",
});

export const HeaderTitle = styled("div")({
  fontFamily: "Manrope",
  fontSize: "16px",
  fontStyle: "normal",
  fontWeight: 700,
  lineHeight: "24px",
  letterSpacing: "0.15px",
  color: colors.gray800,
});

export const HeaderSubtitle = styled("div")({
  fontFamily: "Manrope",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "20px",
  letterSpacing: "0.25px",
  color: colors.gray500,
});

export const ActionSection = styled("div")({
  padding: "12px 16px !important",
  display: "flex",
  gap: "4px",
  justifyContent: "flex-end",
});

export const AccordionContent = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: "4px",
});

export const Content = styled("div")({
  display: "flex",
  flexDirection: "column",
  maxHeight: "400px",
  overflowY: "auto",
  "&::-webkit-scrollbar": {
    width: "4px",
    borderBottomRightRadius: "8px",
    backgroundColor: "transparent",
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: "white",
    borderBottomRightRadius: "8px",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: colors.gray500,
    borderBottomRightRadius: "8px",
  },
});

export const FormControlLabel = styled(MFormControlLabel)(() => ({
  marginLeft: "-8px !important",
  marginRight: "-8px !important",
  marginBottom: "0px !important",
  color: colors.gray600,
  font: "normal 400 16px/24px Manrope",
  letterSpacing: "0.5px",
}));

export const FormBranchLabel = styled("div")(() => ({
  color: colors.gray600,
  font: "normal 400 16px/24px Manrope",
  fontSize: "12px",
  letterSpacing: "0.5px",
}));

export const Checkbox = styled(MCheckbox)(() => ({
  [`&.${checkboxClasses.checked}`]: {
    color: `${colors.brandBlue500} !important`,
  },
}));

export const Radio = styled(MRadio)(() => ({
  [`&.${radioClasses.checked}`]: {
    color: `${colors.brandBlue500} !important`,
  },
}));

export const ResourceTaskPublishFilterContainer = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
  gap: "16px",
}));

export const ResourceTaskPublishFilterLabel = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
  fontFamily: "Manrope",
  fontSize: "16px",
  fontStyle: "normal",
  fontWeight: 600,
  lineHeight: "24px",
  letterSpacing: "0.15px",
  color: colors.gray600,
  gap: "8px",
}));

export const Badge = styled(MBadge)(() => ({
  display: "flex !important",
  [`& .${badgeClasses.badge}`]: {
    backgroundColor: colors.gray500,
    color: colors.white,
    fontWeight: 600,
    lineHeight: "16px",
    fontSize: "11px",
  },
}));

export const Menu = styled(MMenu)(() => ({
  [`& .${menuClasses.paper}`]: {
    borderRadius: "12px",
    width: "auto",
  },
}));

export const MenuItem = styled(MMenuItem, {
  shouldForwardProp(propName) {
    return propName !== "active";
  },
})<{ active?: boolean }>(({ active }) => ({
  padding: "14px 12px",
  display: "flex",
  gap: "12px",
  color: colors.gray600,

  backgroundColor: active ? "#0055A41F" : undefined,

  "&:hover": {
    backgroundColor: "#0055A414",
  },
}));

export const MenuItemLabel = styled("div")(() => ({
  fontFamily: "Manrope",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "20px",
  letterSpacing: "0.25px",
  flex: 1,
}));

export const MenuItemLabelIndicator = styled("div")(() => ({
  fontFamily: "Manrope",
  fontSize: "16px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "24px",
  letterSpacing: "0.5px",
}));

export const MenuItemBranch = styled("div")(() => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
}));
